/**
 * ZRF-EXP-CAP client API helpers (flag-gated on server via EXPENSE_CAPTURE_ENABLED).
 */

import api from '../../api';

export const MAX_EXPENSE_UPLOAD_BYTES = 15 * 1024 * 1024;

/**
 * @param {File} file
 * @param {'camera'|'photo_library'|'file_upload'} sourceType
 * @param {(percent: number) => void} [onProgress]
 */
export async function uploadExpenseDocument(file, sourceType, onProgress) {
  const form = new FormData();
  form.append('file', file);
  form.append('sourceType', sourceType);
  const res = await api.post('/api/expense-documents', form, {
    onUploadProgress: (event) => {
      if (onProgress && event.total) onProgress(Math.round((event.loaded / event.total) * 100));
    }
  });
  return res.data;
}

export async function listExpenseDocuments() {
  const res = await api.get('/api/expense-documents');
  return res.data;
}

/** Includes the draft (or null) and a previewUrl that expires after 15 minutes. */
export async function getExpenseDocument(id) {
  const res = await api.get(`/api/expense-documents/${id}`);
  return res.data;
}

/** Reading a receipt with AI takes several seconds. */
export const EXTRACT_TIMEOUT_MS = 90 * 1000;

/**
 * Reads the document into a draft. A failed read still returns a blank draft.
 * Body must be {}: a null body is sent as JSON "null", which the API's JSON parser rejects.
 */
export async function extractExpenseDocument(id) {
  const res = await api.post(`/api/expense-documents/${id}/extract`, {}, { timeout: EXTRACT_TIMEOUT_MS });
  return res.data;
}

export async function updateExpenseDraft(id, patch) {
  const res = await api.patch(`/api/expense-drafts/${id}`, patch);
  return res.data;
}

/** CRA T776 expense lines: [{ id, label, craLine }]. */
export async function listExpenseCategories() {
  const res = await api.get('/api/expense-drafts/categories');
  return res.data;
}

export async function listProperties() {
  const res = await api.get('/api/properties');
  return res.data;
}

export async function listPropertyUnits(propertyId) {
  const res = await api.get(`/api/properties/${propertyId}/units`);
  return res.data;
}

/** Posts the draft to the expense ledger. Returns { draft, expenseLedgerEntry }. */
export async function approveExpenseDraft(id) {
  const res = await api.post(`/api/expense-drafts/${id}/approve`, {});
  return res.data;
}

/** Posted expenses, newest receipt date first, with property/unit/category names. */
export async function listExpenseLedger() {
  const res = await api.get('/api/expense-ledger');
  return res.data;
}

/** Voids the ledger row (kept for history) and sends the draft back to review. Returns { draft, voidedLedgerEntry }. */
export async function unapproveExpenseDraft(id) {
  const res = await api.post(`/api/expense-drafts/${id}/unapprove`, {});
  return res.data;
}

/** Undoes "not an expense". Returns the draft. */
export async function reopenExpenseDraft(id) {
  const res = await api.post(`/api/expense-drafts/${id}/reopen`, {});
  return res.data;
}

/**
 * Downloads posted expenses as a CSV file for an accountant.
 * @param {{ propertyId?: string, from?: string, to?: string }} params - dates are YYYY-MM-DD, inclusive
 * @returns {Promise<string>} the file name
 */
export async function downloadExpenseLedgerCsv(params = {}) {
  const query = Object.fromEntries(Object.entries(params).filter(([, v]) => v));
  const res = await api.get('/api/expense-ledger/export', { params: query, responseType: 'blob' });
  // Content-Disposition is not readable cross-origin (not in CORS exposed headers), so name it here.
  const range = query.from || query.to
    ? `${query.from || 'start'}-to-${query.to || 'today'}`
    : new Date().toISOString().slice(0, 10);
  const filename = `zenrentflow-expenses-${range}.csv`;
  const url = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
  return filename;
}

/** Marks the draft as not an expense. Returns the draft. */
export async function rejectExpenseDraft(id) {
  const res = await api.post(`/api/expense-drafts/${id}/reject`, {});
  return res.data;
}
